import Vue from 'vue'
import * as types from 'store/mutation-types'
import { isNewQuote } from 'utils/common'
import parseBsTradeRequestUrl from 'utils/parseBsTradeRequestUrl'
import setOptionRequestParams from 'utils/setOptionRequestParams'
import getLocalFixedContract from 'utils/getLocalFixedContract'
import formatOrderLocalToServeParams from 'store/utils/formatOrderLocalToServeParams'
import { ERROR_CODE, ERROR_CODE_OMNIBUS } from 'const/common'
import { SEC_TYPE } from 'const/stock'
import Modal from 'com/Modal'
import getLocalSymbol from 'utils/getLocalSymbol'
import getFixedOrderDetail from 'utils/getLocalFixedOrderDetail'

const state = {
  domScrollable: true, // 闪电下单价格列是否可滚动
  tradeActiveTabId: 'quote', // 交易侧边栏下当前tab
  tradeRequest: null // { stock, order, contract, isModify, isCloseOut }
}

const getters = {
  domScrollable: state => state.domScrollable,
  tradeActiveTabId: state => state.tradeActiveTabId,
  hasModifyOrderRequest: state =>
    state.tradeRequest && state.tradeRequest.isModify,
  hasCloseOutRequest: state =>
    state.tradeRequest && state.tradeRequest.isCloseOut,
  hasTradeRequest: state => !!state.tradeRequest,
  tradeRequest: state => state.tradeRequest
}
const mutations = {
  [types.UPDATE_DOM_SCROLLABLE](state, flag) {
    state.domScrollable = flag
  },
  [types.RESET_TRADE_DOMSCROLLABLE](state) {
    state.domScrollable = true
  },
  [types.UPDATE_TRADE_ACTIVE_TAB_ID](state, id) {
    state.tradeActiveTabId = id
  },
  [types.REMOVE_TRADE_REQUEST](state) {
    state.tradeRequest = null
  },
  [types.NEW_TRADE_REQUEST](state, request) {
    state.tradeRequest = request
  },
  [types.UPDATE_TRADE_STOCK](state, stock) {
    if (isNewQuote(state.tradeRequest.stock, stock)) {
      Object.assign(state.tradeRequest.stock, stock)
    }
  },
  [types.UPDATE_TRADE_ORDER](state, order) {
    if (state.tradeRequest) {
      state.tradeRequest.order = {
        ...state.tradeRequest.order,
        ...order
      }
    }
  }
}
const actions = {
  updateTradeOrder({ commit }, order) {
    commit(types.UPDATE_TRADE_ORDER, order)
  },
  removeTradeRequest({ commit }) {
    commit(types.REMOVE_TRADE_REQUEST)
  },
  newTradeRequest({ commit }, payload) {
    commit(types.NEW_TRADE_REQUEST, payload)
  },
  whenStockUpdate({ commit, getters }, stock) {
    if (!getters.hasTradeRequest) return false
    if (
      !getters.tradeRequest.stock ||
      getters.tradeRequest.stock.secType !== stock.secType
    ) {
      return false
    }
    if (getters.tradeRequest.stock.webLocal.symbol !== stock.webLocal.symbol) {
      return false
    }
    commit(types.UPDATE_TRADE_STOCK, stock)
  },
  // 生成ib交易新的订单id（bs不需要）
  getNewIbTradeOrderId({ getters }) {
    if (getters.isIb) {
      return Vue.http.get('~trade/trade/get_max_order_id').then(res => {
        if (res.data.ret === 0) {
          return res.data.orderId
        }
        throw res.data
      })
    }
  },
  // 获取股票交易合约信息
  getStockContract({ getters }, stock) {
    const payload = { symbol: stock.symbol }
    setOptionRequestParams(payload, stock)
    if (getters.isIb) {
      payload.secType = stock.secType || SEC_TYPE.STK
      return Vue.http
        .get('~trade/trade/get_contract', {
          params: payload
        })
        .then(({ data }) => {
          if (data.ret === 0) {
            return getLocalFixedContract(data, stock, true)
          }
          throw data
        })
        .catch(() => {
          // FIXME: 报错使用默认数据
          return getLocalFixedContract(null, stock, false)
        })
    } else {
      payload.sec_type = stock.secType || SEC_TYPE.OPT
      return Vue.http
        .get(parseBsTradeRequestUrl('/contracts', getters.isVirtual), {
          params: payload
        })
        .then(({ data }) => {
          if (data.status === 'ok') {
            return getLocalFixedContract(data.data, stock, false)
          }
          throw data
        })
        .catch(() => {
          return getLocalFixedContract(null, stock, false)
        })
    }
  },
  // 获取个股持仓
  getHoldingsBySymbol({ getters }, payload) {
    const params = { symbol: payload.symbol }
    if (getters.isIb) {
      params.secType = payload.secType || SEC_TYPE.ALL
      params.symbol = getLocalSymbol(payload)
      return Vue.http
        .get('~trade/portfolio/position/{symbol}', {
          params
        })
        .then(res => {
          if (res.data.ret === 0) {
            return res.data.items || []
          }
          throw res.data
        })
    } else {
      params.sec_type = payload.secType || SEC_TYPE.STK
      if (payload.secType === SEC_TYPE.OPT) {
        setOptionRequestParams(params, payload)
      }
      return Vue.http
        .get(
          parseBsTradeRequestUrl(
            '/portfolio/positions/{symbol}',
            getters.isVirtual
          ),
          { params }
        )
        .then(res => {
          if (res.data.status === 'ok') {
            return res.data.data ? [res.data.data] : []
          }
          throw res.data
        })
    }
  },
  modifyOrder({ getters, commit }, payload) {
    const order = formatOrderLocalToServeParams(
      payload || getters.tradeRequest.order,
      getters.isIb
    )
    if (getters.isIb) {
      return postIbOrders('~trade/trade/modify_order', order)
    }
    return postOmnibusOrders(
      `/orders/${order.id}/modify`,
      getters.isVirtual,
      order
    )
  },
  newOrder({ getters }, payload) {
    const order = formatOrderLocalToServeParams(
      payload || getters.tradeRequest.order,
      getters.isIb
    )
    if (getters.isIb) {
      return postIbOrders('~trade/trade/place_order', order).then(
        getFixedOrderDetail
      )
    }
    return postOmnibusOrders('/orders', getters.isVirtual, order).then(
      getFixedOrderDetail
    )
  },
  cancelOrder({ getters }, orderId) {
    if (getters.isIb) {
      return postIbOrders('~trade/trade/cancel_order', { orderId })
    }
    return postOmnibusOrders(`/orders/${orderId}/cancel`, getters.isVirtual)
  },
  // 订单提交服务端校验
  checkOrder({ getters }, payload) {
    const order = formatOrderLocalToServeParams(payload, getters.isIb)
    if (getters.isIb) {
      return postIbOrders('~trade/trade/preview_order', order)
    }
    return postOmnibusOrders('/orders/check', getters.isVirtual, order)
  },
  getOrderDetail({ getters }, orderId) {
    if (getters.isIb) {
      return Vue.http
        .get('~trade/trade/get_order', {
          params: { orderId }
        })
        .then(({ data: res }) => {
          if (res.ret === 0 && res.items) {
            return getFixedOrderDetail(res.items[0], true)
          }
          throw res
        })
    }
    return Vue.http
      .get(parseBsTradeRequestUrl(`/orders/${orderId}`, getters.isVirtual))
      .then(({ data: res }) => {
        if (res.status === 'ok' && res.data) {
          return getFixedOrderDetail(res.data, false)
        }
        throw res
      })
  }
}

// 发送ib订单请求
function postIbOrders(url, data) {
  return Vue.http.post(url, data).then(res => {
    if (res.data.ret === 0) {
      return res.data.items || []
    }
    if (res.data.ret === ERROR_CODE.TRADE_UNAUTHORIZED) {
      res.data.isNeedReOrder = true
    } else {
      showOrderErrorModalIfNeeded(res.data.msg)
    }
    throw res.data
  })
}

// 发送 omnibus 订单请求
function postOmnibusOrders(url, isVirtual, data) {
  return Vue.http
    .post(parseBsTradeRequestUrl(url, isVirtual), data, { emulateJSON: true })
    .then(res => {
      if (res.data.status === 'ok') {
        return res.data.data || []
      }
      throw res.data
    })
    .catch(err => {
      if (err.data) {
        if (err.data.status === ERROR_CODE_OMNIBUS.TRADE_UNAUTHORIZED) {
          err.data.isNeedReOrder = true
        } else {
          showOrderErrorModalIfNeeded(err.data.msg)
        }
        throw err.data
      }
      throw err
    })
}

// 标准账户错误信息提示处理
function showOrderErrorModalIfNeeded(msg) {
  Modal.open({
    title: Vue.t('tip-title'),
    content: msg || Vue.t('network-error'),
    cancelButton: false
  })
}

export default { state, getters, mutations, actions }
